import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const OPERATOR = {
  platform: "ZEDRO Academy",
  legalName: "Académie ZEDRO",
  addressEs: "9165 Rue Lennon, Suite 412, Brossard, Québec, Canadá, J4Y 0R9",
  addressFr: "9165, rue Lennon, suite 412, Brossard, Québec, Canada, J4Y 0R9",
  supportEmail: "support@zedro.academy",
  adminEmail: "admin@zedro.academy",
  website: "zedro.academy",
};

const PRIVACY_COPY = {
  es: {
    title: "Política de Privacidad",
    lastUpdated: "Última actualización: 28 de junio de 2026",
    intro: [
      "En ZEDRO Academy valoramos la privacidad, la confianza y el cuidado de la información personal. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos, protegemos y compartimos información cuando accedes o utilizas zedro.academy, sus páginas públicas, membresías, herramientas digitales y servicios asociados.",
      "ZEDRO Academy es una plataforma digital operada bajo el nombre comercial registrado Académie ZEDRO en Québec, Canadá. Esta Política aplica a la plataforma ZEDRO Academy, sus páginas públicas, cuentas de usuario, membresías, formularios, herramientas internas, comunicaciones y servicios digitales relacionados.",
    ],
    sections: [
      {
        title: "1. Responsable de privacidad y contacto",
        paragraphs: [
          "Para efectos de esta Política, el responsable de la protección de la información personal tratada por ZEDRO Academy es la administración de Académie ZEDRO, como operador legal de la plataforma.",
        ],
        list: [
          `Plataforma: ${OPERATOR.platform}`,
          `Operador legal: ${OPERATOR.legalName}`,
          `Dirección: ${OPERATOR.addressEs}`,
          `Sitio web: ${OPERATOR.website}`,
          `Correo de soporte: ${OPERATOR.supportEmail}`,
          `Correo administrativo/legal y privacidad: ${OPERATOR.adminEmail}`,
        ],
        paragraphsAfter: [
          "Para solicitudes relacionadas con privacidad, acceso, corrección, eliminación, retiro de consentimiento o preguntas sobre el manejo de información personal, escribe a admin@zedro.academy indicando en el asunto: Privacidad - ZEDRO Academy.",
        ],
      },
      {
        title: "2. Información que podemos recopilar",
        paragraphs: [
          "ZEDRO Academy puede recopilar información personal y técnica según la forma en que uses la plataforma. No todos los datos aplican a todos los usuarios.",
        ],
        groups: [
          {
            subtitle: "2.1 Información de cuenta",
            items: [
              "Nombre o nombre visible, si lo proporcionas.",
              "Correo electrónico.",
              "Contraseña cifrada o credenciales técnicas de autenticación.",
              "Estado de cuenta, rol, permisos, fecha de creación y datos necesarios para administrar el acceso.",
              "Preferencias de idioma, lectura o experiencia dentro de la plataforma.",
            ],
          },
          {
            subtitle: "2.2 Información de membresía y pagos",
            items: [
              "Estado de membresía o suscripción.",
              "Historial de acceso, activación, prueba gratuita, renovación, cancelación o expiración.",
              "Identificadores técnicos relacionados con Stripe u otro proveedor de pago.",
              "Información de facturación, moneda, impuestos, producto adquirido, periodo de facturación y estado de pago cuando sea necesario.",
            ],
          },
          {
            subtitle: "2.3 Información ingresada dentro de la plataforma",
            intro:
              "Algunas herramientas de ZEDRO Academy permiten que el usuario escriba, seleccione, registre o guarde información personal dentro de su cuenta. Esto puede incluir:",
            items: [
              "Respuestas a ejercicios formativos.",
              "Reflexiones personales.",
              "Registros de Bitácora ZEDRO.",
              "Micro-misiones, evidencias, decisiones y avances.",
              "Contenido relacionado con Conexión ZEDRO, como peticiones, palabras, recordatorios o registros de oración.",
              "Preferencias, selecciones, progreso, historial de sistemas y uso de herramientas internas.",
            ],
            outro:
              "El usuario decide qué información ingresar. Recomendamos no incluir información innecesariamente sensible, como datos médicos detallados, números de identificación, información financiera privada, datos de terceros sin autorización o información que no sea necesaria para el uso de la plataforma.",
          },
          {
            subtitle: "2.4 Información técnica y de uso",
            items: [
              "Dirección IP aproximada.",
              "Tipo de dispositivo, navegador, sistema operativo y configuración técnica.",
              "Páginas visitadas, botones utilizados, fecha y hora de acceso.",
              "Errores técnicos, eventos de seguridad y registros necesarios para operar la plataforma.",
              "Cookies, identificadores técnicos o tecnologías similares.",
            ],
          },
          {
            subtitle: "2.5 Comunicaciones",
            items: [
              "Mensajes enviados a soporte o a correos oficiales de ZEDRO Academy.",
              "Solicitudes relacionadas con cuenta, pagos, privacidad, membresía o soporte técnico.",
              "Preferencias de comunicación y consentimiento para recibir mensajes cuando aplique.",
            ],
          },
        ],
      },
      {
        title: "3. Información sensible",
        paragraphs: [
          "ZEDRO Academy no solicita de forma intencional información altamente sensible como diagnósticos médicos, datos financieros privados, documentos de identidad, datos biométricos, información legal delicada o información de terceros sin autorización.",
          "Debido a la naturaleza formativa y espiritual de la plataforma, algunos usuarios pueden decidir escribir reflexiones personales, peticiones, procesos internos o contenido relacionado con su vida de fe. Esa información se trata con especial cuidado dentro de la plataforma y se usa únicamente para prestar el servicio, guardar el progreso, permitir el acceso del usuario a sus registros y mantener la funcionalidad correspondiente.",
          "Si una información no es necesaria para usar ZEDRO Academy, recomendamos no ingresarla.",
        ],
      },
      {
        title: "4. Finalidades del uso de la información",
        paragraphs: ["Utilizamos la información recopilada para las siguientes finalidades:"],
        list: [
          "Crear, administrar y proteger cuentas de usuario.",
          "Dar acceso a contenidos, sistemas, membresías y herramientas digitales.",
          "Guardar progreso, respuestas, registros, bitácora y preferencias.",
          "Permitir el funcionamiento de herramientas como Bitácora ZEDRO, Conexión ZEDRO y otros módulos internos.",
          "Procesar membresías, pagos, renovaciones, cancelaciones y soporte relacionado con facturación.",
          "Enviar comunicaciones necesarias sobre cuenta, seguridad, acceso, pagos, cambios importantes o soporte.",
          "Enviar comunicaciones informativas o promocionales únicamente cuando exista una base válida para hacerlo o el usuario lo haya autorizado.",
          "Mejorar la estabilidad, seguridad, diseño, accesibilidad y funcionamiento de la plataforma.",
          "Analizar uso general de la plataforma de forma agregada o técnica.",
          "Prevenir fraude, abuso, accesos no autorizados, incidentes de seguridad o incumplimientos de términos.",
          "Cumplir obligaciones legales, fiscales, contables, regulatorias o contractuales.",
        ],
      },
      {
        title: "5. Consentimiento y fundamentos del tratamiento",
        paragraphs: [
          "Dependiendo del caso, ZEDRO Academy puede tratar información personal porque es necesaria para prestar el servicio contratado, porque el usuario dio su consentimiento, porque existe una necesidad operativa razonable o porque una obligación legal lo exige.",
          "Cuando el tratamiento dependa del consentimiento, el usuario podrá retirarlo cuando corresponda. Retirar el consentimiento puede afectar ciertas funciones de la plataforma si esa información es necesaria para prestar el servicio.",
          "Cuando una información sea necesaria para operar la cuenta, procesar una membresía, cumplir obligaciones legales, prevenir fraude o conservar registros contables, ZEDRO Academy puede necesitar conservarla o utilizarla conforme a la ley aplicable.",
        ],
      },
      {
        title: "6. Cookies y tecnologías similares",
        paragraphs: [
          "ZEDRO Academy puede utilizar cookies y tecnologías similares para funcionamiento técnico, seguridad, inicio de sesión, preferencias de idioma o lectura, medición de uso, analítica y mejora de la experiencia.",
          "Algunas cookies son necesarias para que la plataforma funcione. Otras, como analítica o medición, pueden depender de tu consentimiento o configuración de preferencias, según corresponda.",
        ],
        link: {
          before: "Puedes consultar más detalles en nuestra ",
          text: "Política de Cookies",
          href: "/legal/cookies",
          after: ".",
        },
      },
      {
        title: "7. Proveedores externos y terceros",
        paragraphs: [
          "Para operar ZEDRO Academy, podemos compartir información limitada con proveedores externos que nos ayudan a prestar el servicio. Estos proveedores deben tratar la información conforme a las finalidades necesarias para prestar sus servicios y de acuerdo con sus propias obligaciones de seguridad y privacidad.",
          "Estos proveedores pueden incluir, entre otros:",
        ],
        list: [
          "Stripe: procesamiento de pagos, pruebas gratuitas con tarjeta, membresías, suscripciones, facturación, prevención de fraude, disputas o contracargos.",
          "Servicios de hosting y base de datos: alojamiento de la plataforma, almacenamiento de información de cuenta, progreso, bitácora y funcionamiento interno.",
          "Servicios de almacenamiento: alojamiento de archivos, imágenes, audios u otros recursos digitales.",
          "Servicios de correo: envío de correos de cuenta, soporte, confirmaciones, seguridad, membresía o comunicaciones autorizadas.",
          "Servicios de analítica: medición de uso, rendimiento, estabilidad y mejora de la experiencia, cuando correspondan y conforme a las preferencias de cookies.",
          "Herramientas de seguridad y monitoreo: detección de errores, prevención de abuso y protección técnica de la plataforma.",
          "Herramientas de inteligencia artificial: solo cuando una función activa de la plataforma requiera asistencia automatizada para apoyar una experiencia específica.",
        ],
      },
      {
        title: "8. Procesamiento fuera de Québec o Canadá",
        paragraphs: [
          "La información de ZEDRO Academy puede almacenarse o procesarse mediante proveedores ubicados en Canadá, Estados Unidos u otros países, según la infraestructura necesaria para operar la plataforma.",
          "Cuando la información sea procesada fuera de Québec o Canadá, ZEDRO Academy procurará utilizar proveedores reconocidos y medidas contractuales, técnicas y organizativas razonables para proteger la información, de acuerdo con la ley aplicable.",
        ],
      },
      {
        title: "9. Pagos, Stripe y datos financieros",
        paragraphs: [
          "Cuando realizas una compra, prueba gratuita con tarjeta, membresía o suscripción, el pago es procesado por Stripe u otro proveedor de pago habilitado. ZEDRO Academy puede recibir y conservar información limitada relacionada con el pago, como estado de suscripción, identificadores de cliente, fecha de cobro, moneda, impuestos, producto adquirido, periodo de facturación, cancelación o estado de pago.",
          "ZEDRO Academy no almacena números completos de tarjeta ni datos completos de seguridad del método de pago. Esa información es gestionada por el proveedor de pago correspondiente.",
          "En caso de disputa, contracargo, fraude, error de cobro o solicitud relacionada con facturación, ZEDRO Academy podrá utilizar información razonablemente necesaria para verificar acceso, aceptación de términos, historial de membresía, uso de la plataforma y comunicaciones relacionadas.",
        ],
      },
      {
        title: "10. Comunicaciones",
        paragraphs: [
          "Podemos enviarte comunicaciones necesarias relacionadas con tu cuenta, acceso, seguridad, cambios importantes, membresía, pagos, soporte, facturación o funcionamiento de la plataforma.",
          "También podemos enviarte comunicaciones informativas, educativas o promocionales cuando hayas dado tu consentimiento, cuando exista una relación válida que lo permita o cuando la ley aplicable lo autorice. Podrás darte de baja de comunicaciones promocionales cuando corresponda.",
        ],
      },
      {
        title: "11. Uso de inteligencia artificial",
        paragraphs: [
          "ZEDRO Academy puede incorporar funciones asistidas por inteligencia artificial para apoyar organización, escritura, reflexión, búsqueda, resumen, acompañamiento general o interacción con herramientas internas.",
          "Cuando utilices una función asistida por IA, la información ingresada puede ser procesada por sistemas automatizados o proveedores externos necesarios para prestar esa función. Recomendamos no ingresar información altamente sensible o innecesaria en herramientas asistidas por IA.",
          "Las respuestas o resultados generados con apoyo de IA pueden contener errores o imprecisiones y no sustituyen asesoría profesional, médica, psicológica, legal, financiera, contable o pastoral personalizada.",
        ],
      },
      {
        title: "12. Conservación de la información",
        paragraphs: [
          "Conservamos la información durante el tiempo necesario para prestar el servicio, mantener la cuenta, guardar el progreso, administrar la membresía, responder solicitudes, resolver disputas, cumplir obligaciones legales, fiscales o contables, prevenir fraude y proteger la seguridad de la plataforma.",
          "Cuando una cuenta sea eliminada o una solicitud de eliminación sea aceptada, ZEDRO Academy tomará medidas razonables para eliminar, anonimizar o desactivar la información correspondiente, salvo aquella que deba conservarse por razones legales, contables, fiscales, de seguridad, resolución de disputas, prevención de fraude o cumplimiento de obligaciones aplicables.",
          "La eliminación de información en copias de seguridad o registros técnicos puede no ser inmediata, pero se gestionará de acuerdo con ciclos razonables de respaldo, seguridad y conservación.",
        ],
      },
      {
        title: "13. Seguridad e incidentes de confidencialidad",
        paragraphs: [
          "ZEDRO Academy implementa medidas técnicas y organizativas razonables para proteger la información personal, incluyendo controles de acceso, uso de proveedores tecnológicos confiables, protección de credenciales, medidas de autenticación, separación de permisos, monitoreo y prácticas de seguridad razonables según la naturaleza del servicio.",
          "Sin embargo, ningún sistema digital puede garantizar seguridad, disponibilidad, integridad o recuperación absoluta. Existen riesgos inherentes al uso de internet, servicios en la nube, proveedores externos, dispositivos personales, errores humanos, fallas técnicas, ataques, pérdida de credenciales o eventos fuera del control razonable de ZEDRO Academy.",
          "En caso de incidente de confidencialidad o seguridad que involucre información personal, ZEDRO Academy tomará medidas razonables para investigar, contener, mitigar riesgos, prevenir incidentes similares y comunicar a usuarios o autoridades cuando corresponda conforme a la ley aplicable.",
        ],
      },
      {
        title: "14. Recuperación, pérdida de acceso y respaldo",
        paragraphs: [
          "ZEDRO Academy procurará mantener la continuidad de la plataforma y aplicar medidas razonables de respaldo o recuperación cuando sea técnicamente posible. Sin embargo, no puede garantizar que toda información ingresada por el usuario sea recuperable en cualquier circunstancia.",
          "Recomendamos que los usuarios conserven copia personal de información especialmente importante que decidan ingresar en la plataforma. En caso de pérdida de acceso, error técnico o incidente, ZEDRO Academy revisará la situación y realizará esfuerzos razonables para restaurar el acceso o la información cuando sea posible.",
        ],
      },
      {
        title: "15. Derechos del usuario",
        paragraphs: [
          "Según la ley aplicable, puedes tener derecho a solicitar acceso, corrección, actualización, eliminación, información sobre el uso de tus datos, retiro de consentimiento o limitación de ciertos usos.",
          "Podemos solicitar información razonable para verificar tu identidad antes de responder. Algunas solicitudes pueden estar limitadas por obligaciones legales, fiscales, contables, seguridad, prevención de fraude, disputas, respaldo técnico o derechos de terceros.",
        ],
        link: {
          before: "Para ejercer estos derechos, escribe a ",
          text: OPERATOR.adminEmail,
          href: `mailto:${OPERATOR.adminEmail}`,
          after: " indicando en el asunto: Privacidad - ZEDRO Academy.",
        },
      },
      {
        title: "16. Menores de edad",
        paragraphs: [
          "ZEDRO Academy está dirigida principalmente a personas adultas. Si una persona menor de edad utiliza la plataforma, debe hacerlo con autorización y supervisión de su madre, padre, tutor legal o adulto responsable, según corresponda.",
          "No buscamos recopilar intencionalmente información de menores sin la autorización correspondiente. Si crees que se ha creado una cuenta o se ha ingresado información de un menor sin autorización, puedes contactarnos para revisar el caso.",
        ],
      },
      {
        title: "17. Enlaces externos",
        paragraphs: [
          "La plataforma puede contener enlaces a sitios, servicios o recursos de terceros. ZEDRO Academy no controla las políticas de privacidad, prácticas de seguridad o contenidos de esos terceros. Recomendamos revisar sus términos y políticas antes de usarlos.",
        ],
      },
      {
        title: "18. Cambios a esta Política",
        paragraphs: [
          "Podemos actualizar esta Política de Privacidad para reflejar cambios legales, técnicos, comerciales, operativos o de funcionamiento de la plataforma. La versión vigente será la publicada en el sitio web con su fecha de actualización.",
          "Si realizamos cambios relevantes, podremos comunicarlo mediante aviso visible en la plataforma, correo electrónico u otro medio razonable.",
        ],
      },
      {
        title: "19. Contacto",
        paragraphs: [
          "Para preguntas, solicitudes o inquietudes relacionadas con esta Política de Privacidad o el manejo de información personal, puedes escribirnos a:",
        ],
        list: [`${OPERATOR.adminEmail}`, `${OPERATOR.supportEmail}`],
      },
    ],
  },
  fr: {
    title: "Politique de confidentialité",
    lastUpdated: "Dernière mise à jour : 28 juin 2026",
    intro: [
      "Chez ZEDRO Academy, nous accordons une grande importance à la confidentialité, à la confiance et à la protection des renseignements personnels. La présente Politique de confidentialité explique comment nous recueillons, utilisons, conservons, protégeons et communiquons des renseignements lorsque vous accédez à zedro.academy ou utilisez ses pages publiques, abonnements, outils numériques et services associés.",
      "ZEDRO Academy est une plateforme numérique exploitée sous le nom commercial enregistré Académie ZEDRO au Québec, Canada. La présente Politique s’applique à la plateforme ZEDRO Academy, à ses pages publiques, comptes utilisateurs, abonnements, formulaires, outils internes, communications et services numériques connexes.",
    ],
    sections: [
      {
        title: "1. Responsable de la confidentialité et coordonnées",
        paragraphs: [
          "Aux fins de la présente Politique, la responsabilité de la protection des renseignements personnels traités par ZEDRO Academy relève de l’administration d’Académie ZEDRO, en tant qu’exploitant légal de la plateforme.",
        ],
        list: [
          `Plateforme : ${OPERATOR.platform}`,
          `Exploitant légal : ${OPERATOR.legalName}`,
          `Adresse : ${OPERATOR.addressFr}`,
          `Site Web : ${OPERATOR.website}`,
          `Courriel d’assistance : ${OPERATOR.supportEmail}`,
          `Courriel administratif/juridique et confidentialité : ${OPERATOR.adminEmail}`,
        ],
        paragraphsAfter: [
          "Pour toute demande liée à la confidentialité, à l’accès, à la rectification, à la suppression, au retrait du consentement ou au traitement des renseignements personnels, veuillez écrire à admin@zedro.academy en indiquant dans l’objet : Confidentialité - ZEDRO Academy.",
        ],
      },
      {
        title: "2. Renseignements que nous pouvons recueillir",
        paragraphs: [
          "ZEDRO Academy peut recueillir des renseignements personnels et techniques selon la façon dont vous utilisez la plateforme. Tous les renseignements ne s’appliquent pas à tous les utilisateurs.",
        ],
        groups: [
          {
            subtitle: "2.1 Renseignements de compte",
            items: [
              "Nom ou nom affiché, si vous le fournissez.",
              "Adresse courriel.",
              "Mot de passe chiffré ou identifiants techniques d’authentification.",
              "État du compte, rôle, permissions, date de création et données nécessaires à la gestion de l’accès.",
              "Préférences de langue, de lecture ou d’expérience dans la plateforme.",
            ],
          },
          {
            subtitle: "2.2 Renseignements d’abonnement et de paiement",
            items: [
              "État de l’abonnement ou de l’adhésion.",
              "Historique d’accès, d’activation, d’essai gratuit, de renouvellement, d’annulation ou d’expiration.",
              "Identifiants techniques liés à Stripe ou à un autre fournisseur de paiement.",
              "Renseignements de facturation, devise, taxes, produit acheté, période de facturation et état du paiement lorsque cela est nécessaire.",
            ],
          },
          {
            subtitle: "2.3 Renseignements saisis dans la plateforme",
            intro:
              "Certains outils de ZEDRO Academy permettent à l’utilisateur d’écrire, de sélectionner, d’enregistrer ou de conserver des renseignements personnels dans son compte. Cela peut inclure :",
            items: [
              "Réponses aux exercices formatifs.",
              "Réflexions personnelles.",
              "Registres de Bitácora ZEDRO.",
              "Micro-missions, preuves, décisions et progrès.",
              "Contenu lié à Conexión ZEDRO, comme des demandes, paroles, rappels ou registres de prière.",
              "Préférences, sélections, progression, historique des systèmes et utilisation des outils internes.",
            ],
            outro:
              "L’utilisateur décide quels renseignements saisir. Nous recommandons de ne pas inclure de renseignements inutilement sensibles, tels que des données médicales détaillées, des numéros d’identification, des renseignements financiers privés, des données de tiers sans autorisation ou des renseignements qui ne sont pas nécessaires à l’utilisation de la plateforme.",
          },
          {
            subtitle: "2.4 Renseignements techniques et d’utilisation",
            items: [
              "Adresse IP approximative.",
              "Type d’appareil, navigateur, système d’exploitation et configuration technique.",
              "Pages visitées, boutons utilisés, date et heure d’accès.",
              "Erreurs techniques, événements de sécurité et journaux nécessaires au fonctionnement de la plateforme.",
              "Cookies, identifiants techniques ou technologies similaires.",
            ],
          },
          {
            subtitle: "2.5 Communications",
            items: [
              "Messages envoyés au soutien ou aux courriels officiels de ZEDRO Academy.",
              "Demandes liées au compte, aux paiements, à la confidentialité, à l’abonnement ou au soutien technique.",
              "Préférences de communication et consentement à recevoir des messages, le cas échéant.",
            ],
          },
        ],
      },
      {
        title: "3. Renseignements sensibles",
        paragraphs: [
          "ZEDRO Academy ne demande pas intentionnellement des renseignements hautement sensibles tels que des diagnostics médicaux, des données financières privées, des documents d’identité, des données biométriques, des renseignements juridiques délicats ou des renseignements concernant des tiers sans autorisation.",
          "En raison de la nature formative et spirituelle de la plateforme, certains utilisateurs peuvent décider d’écrire des réflexions personnelles, des demandes, des processus intérieurs ou du contenu lié à leur vie de foi. Ces renseignements sont traités avec un soin particulier dans la plateforme et sont utilisés uniquement pour fournir le service, conserver la progression, permettre à l’utilisateur d’accéder à ses registres et maintenir la fonctionnalité correspondante.",
          "Si un renseignement n’est pas nécessaire pour utiliser ZEDRO Academy, nous recommandons de ne pas le saisir.",
        ],
      },
      {
        title: "4. Finalités de l’utilisation des renseignements",
        paragraphs: ["Nous utilisons les renseignements recueillis aux fins suivantes :"],
        list: [
          "Créer, administrer et protéger les comptes utilisateurs.",
          "Donner accès aux contenus, systèmes, abonnements et outils numériques.",
          "Conserver la progression, les réponses, les registres, la bitácora et les préférences.",
          "Permettre le fonctionnement d’outils comme Bitácora ZEDRO, Conexión ZEDRO et d’autres modules internes.",
          "Traiter les abonnements, paiements, renouvellements, annulations et le soutien lié à la facturation.",
          "Envoyer les communications nécessaires concernant le compte, la sécurité, l’accès, les paiements, les changements importants ou le soutien.",
          "Envoyer des communications informatives ou promotionnelles uniquement lorsqu’il existe une base valable pour le faire ou lorsque l’utilisateur l’a autorisé.",
          "Améliorer la stabilité, la sécurité, le design, l’accessibilité et le fonctionnement de la plateforme.",
          "Analyser l’utilisation générale de la plateforme de façon agrégée ou technique.",
          "Prévenir la fraude, les abus, les accès non autorisés, les incidents de sécurité ou les manquements aux conditions.",
          "Respecter les obligations légales, fiscales, comptables, réglementaires ou contractuelles.",
        ],
      },
      {
        title: "5. Consentement et fondements du traitement",
        paragraphs: [
          "Selon le cas, ZEDRO Academy peut traiter des renseignements personnels parce que cela est nécessaire pour fournir le service demandé, parce que l’utilisateur a donné son consentement, parce qu’il existe une nécessité opérationnelle raisonnable ou parce qu’une obligation légale l’exige.",
          "Lorsque le traitement repose sur le consentement, l’utilisateur peut le retirer lorsque cela s’applique. Le retrait du consentement peut affecter certaines fonctions de la plateforme si les renseignements concernés sont nécessaires à la prestation du service.",
          "Lorsqu’un renseignement est nécessaire pour exploiter le compte, traiter un abonnement, respecter des obligations légales, prévenir la fraude ou conserver des registres comptables, ZEDRO Academy peut devoir le conserver ou l’utiliser conformément à la loi applicable.",
        ],
      },
      {
        title: "6. Cookies et technologies similaires",
        paragraphs: [
          "ZEDRO Academy peut utiliser des cookies et technologies similaires pour le fonctionnement technique, la sécurité, la connexion, les préférences de langue ou de lecture, la mesure de l’utilisation, l’analytique et l’amélioration de l’expérience.",
          "Certains cookies sont nécessaires au fonctionnement de la plateforme. D’autres, comme ceux liés à l’analytique ou à la mesure, peuvent dépendre de votre consentement ou de vos préférences, selon le cas.",
        ],
        link: {
          before: "Vous pouvez consulter plus de détails dans notre ",
          text: "Politique de cookies",
          href: "/legal/cookies",
          after: ".",
        },
      },
      {
        title: "7. Fournisseurs externes et tiers",
        paragraphs: [
          "Pour exploiter ZEDRO Academy, nous pouvons partager des renseignements limités avec des fournisseurs externes qui nous aident à fournir le service. Ces fournisseurs doivent traiter les renseignements conformément aux finalités nécessaires à la prestation de leurs services et selon leurs propres obligations de sécurité et de confidentialité.",
          "Ces fournisseurs peuvent comprendre, entre autres :",
        ],
        list: [
          "Stripe : traitement des paiements, essais gratuits avec carte, abonnements, facturation, prévention de la fraude, litiges ou rétrofacturations.",
          "Services d’hébergement et de base de données : hébergement de la plateforme, stockage des renseignements de compte, de progression, de bitácora et fonctionnement interne.",
          "Services de stockage : hébergement de fichiers, images, audios ou autres ressources numériques.",
          "Services de courriel : envoi de courriels de compte, de soutien, de confirmations, de sécurité, d’abonnement ou de communications autorisées.",
          "Services d’analytique : mesure de l’utilisation, du rendement, de la stabilité et de l’amélioration de l’expérience, le cas échéant et conformément aux préférences de cookies.",
          "Outils de sécurité et de surveillance : détection d’erreurs, prévention des abus et protection technique de la plateforme.",
          "Outils d’intelligence artificielle : uniquement lorsqu’une fonction active de la plateforme nécessite une assistance automatisée pour soutenir une expérience précise.",
        ],
      },
      {
        title: "8. Traitement hors Québec ou hors Canada",
        paragraphs: [
          "Les renseignements de ZEDRO Academy peuvent être stockés ou traités au moyen de fournisseurs situés au Canada, aux États-Unis ou dans d’autres pays, selon l’infrastructure nécessaire à l’exploitation de la plateforme.",
          "Lorsque des renseignements sont traités hors Québec ou hors Canada, ZEDRO Academy s’efforcera d’utiliser des fournisseurs reconnus ainsi que des mesures contractuelles, techniques et organisationnelles raisonnables afin de protéger les renseignements, conformément à la loi applicable.",
        ],
      },
      {
        title: "9. Paiements, Stripe et données financières",
        paragraphs: [
          "Lorsque vous effectuez un achat, commencez un essai gratuit avec carte, souscrivez à une adhésion ou à un abonnement, le paiement est traité par Stripe ou par un autre fournisseur de paiement activé. ZEDRO Academy peut recevoir et conserver des renseignements limités liés au paiement, comme l’état de l’abonnement, les identifiants client, la date de paiement, la devise, les taxes, le produit acheté, la période de facturation, l’annulation ou l’état du paiement.",
          "ZEDRO Academy ne conserve pas les numéros complets de carte ni les données complètes de sécurité du mode de paiement. Ces renseignements sont gérés par le fournisseur de paiement concerné.",
          "En cas de litige, rétrofacturation, fraude, erreur de paiement ou demande liée à la facturation, ZEDRO Academy pourra utiliser les renseignements raisonnablement nécessaires pour vérifier l’accès, l’acceptation des conditions, l’historique d’abonnement, l’utilisation de la plateforme et les communications connexes.",
        ],
      },
      {
        title: "10. Communications",
        paragraphs: [
          "Nous pouvons vous envoyer des communications nécessaires liées à votre compte, à l’accès, à la sécurité, aux changements importants, à l’abonnement, aux paiements, au soutien, à la facturation ou au fonctionnement de la plateforme.",
          "Nous pouvons aussi vous envoyer des communications informatives, éducatives ou promotionnelles lorsque vous avez donné votre consentement, lorsqu’une relation valable le permet ou lorsque la loi applicable l’autorise. Vous pourrez vous désabonner des communications promotionnelles lorsque cela s’applique.",
        ],
      },
      {
        title: "11. Utilisation de l’intelligence artificielle",
        paragraphs: [
          "ZEDRO Academy peut intégrer des fonctions assistées par intelligence artificielle pour soutenir l’organisation, l’écriture, la réflexion, la recherche, le résumé, l’accompagnement général ou l’interaction avec des outils internes.",
          "Lorsque vous utilisez une fonction assistée par IA, les renseignements saisis peuvent être traités par des systèmes automatisés ou par des fournisseurs externes nécessaires à la prestation de cette fonction. Nous recommandons de ne pas saisir de renseignements hautement sensibles ou inutiles dans les outils assistés par IA.",
          "Les réponses ou résultats générés avec l’appui de l’IA peuvent contenir des erreurs ou des imprécisions et ne remplacent pas un conseil professionnel, médical, psychologique, juridique, financier, comptable ou pastoral personnalisé.",
        ],
      },
      {
        title: "12. Conservation des renseignements",
        paragraphs: [
          "Nous conservons les renseignements pendant la durée nécessaire pour fournir le service, maintenir le compte, conserver la progression, administrer l’abonnement, répondre aux demandes, résoudre les litiges, respecter les obligations légales, fiscales ou comptables, prévenir la fraude et protéger la sécurité de la plateforme.",
          "Lorsqu’un compte est supprimé ou qu’une demande de suppression est acceptée, ZEDRO Academy prendra des mesures raisonnables pour supprimer, anonymiser ou désactiver les renseignements concernés, sauf ceux qui doivent être conservés pour des raisons légales, comptables, fiscales, de sécurité, de résolution de litiges, de prévention de la fraude ou de respect des obligations applicables.",
          "La suppression des renseignements dans les copies de sauvegarde ou les journaux techniques peut ne pas être immédiate, mais elle sera gérée selon des cycles raisonnables de sauvegarde, de sécurité et de conservation.",
        ],
      },
      {
        title: "13. Sécurité et incidents de confidentialité",
        paragraphs: [
          "ZEDRO Academy met en place des mesures techniques et organisationnelles raisonnables pour protéger les renseignements personnels, notamment des contrôles d’accès, l’utilisation de fournisseurs technologiques fiables, la protection des identifiants, des mesures d’authentification, la séparation des permissions, la surveillance et des pratiques de sécurité raisonnables selon la nature du service.",
          "Toutefois, aucun système numérique ne peut garantir une sécurité, une disponibilité, une intégrité ou une récupération absolue. Il existe des risques inhérents à l’utilisation d’Internet, des services infonuagiques, des fournisseurs externes, des appareils personnels, des erreurs humaines, des défaillances techniques, des attaques, de la perte d’identifiants ou des événements hors du contrôle raisonnable de ZEDRO Academy.",
          "En cas d’incident de confidentialité ou de sécurité impliquant des renseignements personnels, ZEDRO Academy prendra des mesures raisonnables pour enquêter, contenir l’incident, atténuer les risques, prévenir des incidents similaires et communiquer avec les utilisateurs ou les autorités lorsque cela est requis par la loi applicable.",
        ],
      },
      {
        title: "14. Récupération, perte d’accès et sauvegarde",
        paragraphs: [
          "ZEDRO Academy s’efforcera de maintenir la continuité de la plateforme et d’appliquer des mesures raisonnables de sauvegarde ou de récupération lorsque cela est techniquement possible. Toutefois, elle ne peut pas garantir que tous les renseignements saisis par l’utilisateur seront récupérables en toute circonstance.",
          "Nous recommandons aux utilisateurs de conserver une copie personnelle des renseignements particulièrement importants qu’ils décident de saisir dans la plateforme. En cas de perte d’accès, d’erreur technique ou d’incident, ZEDRO Academy examinera la situation et fera des efforts raisonnables pour restaurer l’accès ou les renseignements lorsque cela est possible.",
        ],
      },
      {
        title: "15. Droits de l’utilisateur",
        paragraphs: [
          "Selon la loi applicable, vous pouvez avoir le droit de demander l’accès, la rectification, la mise à jour, la suppression, des renseignements sur l’utilisation de vos données, le retrait du consentement ou la limitation de certaines utilisations.",
          "Nous pouvons demander des renseignements raisonnables afin de vérifier votre identité avant de répondre. Certaines demandes peuvent être limitées par des obligations légales, fiscales, comptables, de sécurité, de prévention de la fraude, de litiges, de sauvegarde technique ou par les droits de tiers.",
        ],
        link: {
          before: "Pour exercer ces droits, écrivez à ",
          text: OPERATOR.adminEmail,
          href: `mailto:${OPERATOR.adminEmail}`,
          after: " en indiquant dans l’objet : Confidentialité - ZEDRO Academy.",
        },
      },
      {
        title: "16. Personnes mineures",
        paragraphs: [
          "ZEDRO Academy s’adresse principalement aux personnes adultes. Si une personne mineure utilise la plateforme, elle doit le faire avec l’autorisation et la supervision de sa mère, de son père, de son tuteur légal ou d’un adulte responsable, selon le cas.",
          "Nous ne cherchons pas à recueillir intentionnellement des renseignements de personnes mineures sans l’autorisation appropriée. Si vous croyez qu’un compte a été créé ou que des renseignements d’une personne mineure ont été saisis sans autorisation, vous pouvez nous contacter afin que le cas soit examiné.",
        ],
      },
      {
        title: "17. Liens externes",
        paragraphs: [
          "La plateforme peut contenir des liens vers des sites, services ou ressources de tiers. ZEDRO Academy ne contrôle pas les politiques de confidentialité, les pratiques de sécurité ou les contenus de ces tiers. Nous recommandons de consulter leurs conditions et politiques avant de les utiliser.",
        ],
      },
      {
        title: "18. Modifications de la présente Politique",
        paragraphs: [
          "Nous pouvons mettre à jour la présente Politique de confidentialité afin de refléter des changements légaux, techniques, commerciaux, opérationnels ou liés au fonctionnement de la plateforme. La version en vigueur sera celle publiée sur le site Web avec sa date de mise à jour.",
          "Si nous apportons des changements importants, nous pourrons les communiquer au moyen d’un avis visible dans la plateforme, par courriel ou par tout autre moyen raisonnable.",
        ],
      },
      {
        title: "19. Contact",
        paragraphs: [
          "Pour toute question, demande ou préoccupation liée à la présente Politique de confidentialité ou au traitement des renseignements personnels, vous pouvez nous écrire à :",
        ],
        list: [`${OPERATOR.adminEmail}`, `${OPERATOR.supportEmail}`],
      },
    ],
  },
};

const pageStyle = {
  maxWidth: 900,
  margin: "2rem auto",
  padding: "0 1rem",
  fontFamily: "Questrial, sans-serif",
  lineHeight: 1.75,
  color: "#2c3b51",
};

const headingStyle = { color: "#286b94" };

function renderParagraphs(paragraphs = []) {
  return paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>);
}

function renderList(items = []) {
  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

function renderLink(link) {
  if (!link) return null;

  return (
    <p>
      {link.before}
      <a href={link.href}>{link.text}</a>
      {link.after}
    </p>
  );
}

function renderGroups(groups = []) {
  return groups.map((group, index) => (
    <div key={index}>
      <h3>{group.subtitle}</h3>
      {group.intro && <p>{group.intro}</p>}
      {renderList(group.items)}
      {group.outro && <p>{group.outro}</p>}
    </div>
  ));
}

export default function PrivacyPolicy() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const copy = PRIVACY_COPY[language] || PRIVACY_COPY.es;

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return (
    <div style={pageStyle}>
      <h1>{copy.title}</h1>

      <p>
        <strong>{copy.lastUpdated}</strong>
      </p>

      {renderParagraphs(copy.intro)}

      {copy.sections.map((section) => (
        <section key={section.title}>
          <h2 style={headingStyle}>{section.title}</h2>
          {renderParagraphs(section.paragraphs)}
          {section.list && renderList(section.list)}
          {section.groups && renderGroups(section.groups)}
          {renderLink(section.link)}
          {renderParagraphs(section.paragraphsAfter)}
        </section>
      ))}
    </div>
  );
}
